import { useSelector } from 'react-redux'
import { VWOApiOptions } from 'vwo-node-sdk'

import { getUserIdentity } from '~/redux-modules/identity'

import { useVwoContext } from './VwoProvider'

export type VwoFeatureResult<Variables extends Record<string, unknown>> =
    | {
          enabled: true
          variables: Variables
      }
    | {
          enabled: false
      }

export interface UseGetVwoFeatureArguments<
    Variables extends Record<string, unknown>,
    Keys,
> {
    featureName: string
    /**
     * Default value for feature if for any reason we can't get it from VWO
     */
    defaultValue: VwoFeatureResult<Variables>
    /**
     * list of variables keys you want to retrieve associated with feature
     */
    variableKeys?: Keys[]
    /**
     * options for vwo getFeatureVariableValue
     */
    vwoOptions?: VWOApiOptions
}

/**
 * Returns enabled/disabled state along with possible variables
 */
export const useGetVwoFeature = <
    Variables extends Record<string, any>,
    Keys extends Extract<keyof Variables, string>,
>({
    featureName,
    variableKeys,
    vwoOptions,
    defaultValue,
}: UseGetVwoFeatureArguments<Variables, Keys>): VwoFeatureResult<
    Pick<Variables, Keys>
> => {
    const { vwoClient } = useVwoContext()
    const userId = String(useSelector(state => getUserIdentity(state).id))

    if (
        (vwoClient && !vwoClient.isFeatureEnabled(featureName, userId)) ||
        !defaultValue.enabled
    ) {
        return {
            enabled: false,
        }
    }

    const result: VwoFeatureResult<Record<string, unknown>> = defaultValue

    /**
     * Pick only requested variables
     */
    if (variableKeys?.length) {
        variableKeys.forEach(variableKey => {
            result.variables[variableKey] =
                vwoClient?.getFeatureVariableValue(
                    featureName,
                    variableKey,
                    userId,
                    vwoOptions,
                ) || result.variables[variableKey]
        })
    }

    return result as VwoFeatureResult<Pick<Variables, Keys>>
}
